import { ReactNode, useEffect } from "react";
import { get, isEmpty, map } from "lodash";
import { isBefore, parse } from "date-fns";
import {
  FieldValues,
  UseFieldArrayReturn,
  useFormContext,
} from "react-hook-form";
import DateTimePicker from "../../shared/form/fields/DateTimePicker";
import ScheduleTimeField from "./ScheduleTimeField";
import classNames from "classnames";

type ScheduleFieldsArgs = {
  fields: UseFieldArrayReturn["fields"];
  removeButton: (index: number) => ReactNode;
};

export default function ScheduleFields({
  fields,
  removeButton,
}: ScheduleFieldsArgs) {
  const {
    formState: { errors },
    watch,
  } = useFormContext();

  return (
    <>
      {map(fields, (field, index) => {
        const date = watch(`object.proposals.${index}.date`);
        return (
          <ScheduleField
            key={field.id}
            dateName={`object.proposals.${index}.date`}
            timeName={`object.proposals.${index}.time`}
            timeEndName={`object.proposals.${index}.time_end`}
            removeButton={() => removeButton(index)}
            dateInvalid={
              !!get(errors, ["object", "proposals", "root", "message"]) &&
              isEmpty(date)
            }
          />
        );
      })}
    </>
  );
}

type ScheduleFieldArgs = {
  dateName: string;
  timeName: string;
  timeEndName: string;
  removeButton?: () => ReactNode;
  allowEmptyTime?: boolean;
  dateInvalid?: boolean;
  dateRequired?: string;
  disabled?: boolean;
  labels?: { date: string; time: string; timeEnd: string };
};

export function ScheduleField({
  dateName,
  timeName,
  timeEndName,
  removeButton,
  allowEmptyTime,
  dateInvalid = false,
  dateRequired,
  disabled = false,
  labels,
}: ScheduleFieldArgs) {
  const { getFieldState, formState, watch, getValues, setValue } =
    useFormContext();
  const timeEndError = getFieldState(timeEndName, formState).error;
  const time = watch(timeName);

  // an end only shows next to a start, so it goes along when the start is emptied
  useEffect(() => {
    if (isEmpty(time) && !isEmpty(getValues(timeEndName)))
      setValue(timeEndName, undefined);
  }, [time, timeEndName, getValues, setValue]);

  function validateTimeEnd(timeEnd: string | undefined, values: FieldValues) {
    const time = get(values, timeName);
    if (isEmpty(time) || isEmpty(timeEnd)) return true;

    // a fixed day without a DST switch, only the clock time matters
    const reference = new Date(2000, 0, 1);
    return (
      !isBefore(
        parse(timeEnd as string, "HH:mm", reference),
        parse(time, "HH:mm", reference),
      ) || I18n.t("js.activity.validation.new_proposal.time_end.before_start")
    );
  }

  return (
    // the times keep their width, the day takes the rest: fractions left gaps beside them in wider forms
    <div className="add-schedule-option grid grid-cols-[minmax(0,1fr)_auto_auto_auto_auto] gap-2 items-center">
      <DateTimePicker
        name={dateName}
        dateClassName={classNames("w-full", {
          "field-error field-error-live": dateInvalid,
        })}
        dateAriaLabel={labels?.date}
        required={dateRequired}
        disabled={disabled}
      />
      <ScheduleTimeField
        name={timeName}
        dateName={dateName}
        allowEmpty={allowEmptyTime}
        ariaLabel={labels?.time}
        disabled={disabled}
        rules={{ deps: [timeEndName] }}
      />
      -
      <ScheduleTimeField
        name={timeEndName}
        dateName={dateName}
        allowEmpty
        ariaLabel={labels?.timeEnd}
        rules={{ validate: validateTimeEnd }}
        disabled={disabled || isEmpty(time)}
      />
      {removeButton && removeButton()}
      {timeEndError?.message && (
        <div className="col-start-2 col-span-4 text-sm text-danger">
          {timeEndError.message}
        </div>
      )}
    </div>
  );
}
